import { Injectable, OnDestroy } from '@angular/core';
import { Metadata } from '@memberjunction/core';
import { UUIDsEqual } from '@memberjunction/global';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { IzzyMetadataEngine } from 'mj_generatedentities';

/** Shape of one presence event as delivered by the `presence` subscription. */
interface PresenceNotification {
  PersonID: string;
  PersonName?: string;
  Action: string;
}

/**
 * Live "who else is viewing this record" presence for one record at a time.
 *
 * Rides MJAPI's PresenceResolver (PRD v4.0 §11.5; realtime-messages PRD §5.4) over MJ's Pub/Sub. The
 * server owns identity and scope: WHO we are comes from our login, and events for a record only reach
 * members of the organisation that owns it. We pass the organisation as a subscription argument
 * because a websocket carries no tenant context; the server verifies it against our memberships.
 *
 * Presence is advisory and ephemeral: announce on open, heartbeat while open, and drop any viewer not
 * heard from within the TTL — so a closed tab or dropped connection falls off on its own.
 *
 * NOT providedIn root: provide it on the component that uses it (`providers: [IzzyPresenceService]`)
 * so each screen instance gets its own session and Angular stops it when that component is destroyed.
 *
 * When someone new announces themselves we answer with our own 'viewing' at once (state sync on join,
 * peer style) so the newcomer sees us immediately instead of after our next heartbeat. It cannot
 * ping-pong: the newcomer already has us in their map when the answer lands, so they do not reply.
 *
 * Unlike the original ticket-detail implementation, this keeps itself alive. MJ's GraphQL provider
 * COMPLETES a subscription on token expiry and can quietly close a long-lived socket, so:
 *   - the stream is re-opened whenever it completes or errors, and
 *   - our own heartbeat doubles as a health check — it comes back to us through the subscription, so
 *     a missing echo means the subscription is dead and gets re-opened.
 */
@Injectable()
export class IzzyPresenceService implements OnDestroy {
  // TTL >= 2x heartbeat (one missed beat never flickers a viewer out); echo timeout > TTL.
  private static readonly HEARTBEAT_MS = 10_000;
  private static readonly TTL_MS = 25_000;          // a viewer not heard from in this window is dropped
  private static readonly ECHO_TIMEOUT_MS = 35_000; // no echo of our own heartbeat for this long = dead stream
  private static readonly SECOND_HELLO_MS = 1_500;  // re-announce once shortly after opening, in case our subscription was not yet live when others replied
  private static readonly RESUBSCRIBE_MIN_MS = 2_000;
  private static readonly RESUBSCRIBE_MAX_MS = 30_000;

  private static readonly SCOPE = 'message';
  private static readonly PRESENCE_SUB = `
    subscription Presence($organizationID: String!, $scope: String!, $recordID: String!) {
      presence(organizationID: $organizationID, scope: $scope, recordID: $recordID) { RecordID PersonID PersonName Action At }
    }`;
  private static readonly PRESENCE_PUB = `
    mutation AnnouncePresence($scope: String!, $recordID: String!, $action: String!) {
      announcePresence(scope: $scope, recordID: $recordID, action: $action)
    }`;

  private viewers = new Map<string, { name: string; lastSeen: number }>();
  private viewerNames: string[] = [];

  private recordId: string | null = null;        // the record we announced presence on
  private organizationId: string | null = null;  // the organisation that owns it (subscription scope)
  private personId: string | null = null;        // our own BC Person ID, used to ignore our echoes
  private stream?: { unsubscribe(): void };
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private sweepTimer?: ReturnType<typeof setInterval>;
  private resubscribeTimer?: ReturnType<typeof setTimeout>;
  private secondHelloTimer?: ReturnType<typeof setTimeout>;
  private resubscribeDelay = IzzyPresenceService.RESUBSCRIBE_MIN_MS;
  private lastEchoAt = 0;

  /** Display names of the other people currently viewing the watched record. */
  get otherViewers(): string[] {
    return this.viewerNames;
  }

  /**
   * Watch a record: leave whatever was being watched, then announce + listen on the new one.
   * `organizationId` is the organisation that owns the record (the server rejects a mismatch).
   * Pass a null record to stop. A no-op when the record is unchanged, so it is safe to call on
   * every selection change.
   */
  watch(recordId: string | null, organizationId: string | null): void {
    if (UUIDsEqual(this.recordId, recordId)) return;
    this.stop();
    if (recordId && organizationId) this.start(recordId, organizationId);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private start(recordId: string, organizationId: string): void {
    const personId = IzzyMetadataEngine.Instance.CurrentContactID ?? null;
    if (!personId) return;  // no BC person yet: we could not tell our own echoes apart, so sit out
    this.recordId = recordId;
    this.organizationId = organizationId;
    this.personId = personId;
    this.lastEchoAt = Date.now();

    this.subscribe(recordId);
    void this.announce('viewing', recordId);
    // The subscription and the announce race: if others answer before our subscription is registered
    // on the server we miss the reply. A second hello shortly after covers that without waiting a beat.
    this.secondHelloTimer = setTimeout(() => { this.secondHelloTimer = undefined; if (this.isWatching(recordId)) void this.announce('viewing', recordId); }, IzzyPresenceService.SECOND_HELLO_MS);
    this.heartbeatTimer = setInterval(() => void this.announce('viewing', recordId), IzzyPresenceService.HEARTBEAT_MS);
    this.sweepTimer = setInterval(() => this.sweep(recordId), IzzyPresenceService.HEARTBEAT_MS);
  }

  /** Announce 'left', close the stream, clear timers and viewers. Safe to call repeatedly. */
  private stop(): void {
    if (this.recordId) {
      void this.announce('left', this.recordId);
    }
    this.closeStream();
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = undefined; }
    if (this.sweepTimer) { clearInterval(this.sweepTimer); this.sweepTimer = undefined; }
    if (this.resubscribeTimer) { clearTimeout(this.resubscribeTimer); this.resubscribeTimer = undefined; }
    if (this.secondHelloTimer) { clearTimeout(this.secondHelloTimer); this.secondHelloTimer = undefined; }
    this.resubscribeDelay = IzzyPresenceService.RESUBSCRIBE_MIN_MS;
    this.viewers.clear();
    this.viewerNames = [];
    this.recordId = null;
    this.organizationId = null;
    this.personId = null;
  }

  private subscribe(recordId: string): void {
    this.closeStream();
    try {
      const provider = Metadata.Provider as GraphQLDataProvider;
      const vars = { organizationID: this.organizationId, scope: IzzyPresenceService.SCOPE, recordID: recordId };
      this.stream = provider.subscribe(IzzyPresenceService.PRESENCE_SUB, vars).subscribe({
        next: (data: { presence?: PresenceNotification }) => this.onNotification(recordId, data?.presence),
        error: (e: unknown) => {
          console.error('IzzyPresence: subscription error', e);
          this.scheduleResubscribe(recordId);
        },
        // The provider completes (does not error) the stream when the token expires.
        complete: () => this.scheduleResubscribe(recordId),
      });
    } catch (e) {
      console.error('IzzyPresence: failed to subscribe', e);
      this.scheduleResubscribe(recordId);
    }
  }

  private onNotification(recordId: string, n: PresenceNotification | undefined): void {
    if (!n || !this.isWatching(recordId)) return;
    this.resubscribeDelay = IzzyPresenceService.RESUBSCRIBE_MIN_MS;  // the stream is delivering

    if (UUIDsEqual(n.PersonID, this.personId)) {
      this.lastEchoAt = Date.now();  // our own heartbeat came back: the stream is healthy
      return;
    }
    const key = n.PersonID.toLowerCase();
    if (n.Action === 'left') {
      this.viewers.delete(key);
    } else {
      const isNewcomer = !this.viewers.has(key);
      this.viewers.set(key, { name: n.PersonName || 'A teammate', lastSeen: Date.now() });
      if (isNewcomer) {
        void this.announce('viewing', recordId);  // answer the newcomer right away
      }
    }
    this.refreshViewerNames();
  }

  /** Re-open the stream after a delay (backing off), unless we have moved on to another record. */
  private scheduleResubscribe(recordId: string): void {
    if (!this.isWatching(recordId) || this.resubscribeTimer) return;
    const delay = this.resubscribeDelay;
    this.resubscribeDelay = Math.min(delay * 2, IzzyPresenceService.RESUBSCRIBE_MAX_MS);
    this.resubscribeTimer = setTimeout(() => {
      this.resubscribeTimer = undefined;
      if (!this.isWatching(recordId)) return;
      this.lastEchoAt = Date.now();
      this.subscribe(recordId);
    }, delay);
  }

  /** Expire viewers past the TTL, and re-open the stream if our own heartbeats stopped coming back. */
  private sweep(recordId: string): void {
    if (!this.isWatching(recordId)) return;
    const now = Date.now();
    let changed = false;
    for (const [key, viewer] of this.viewers) {
      if (viewer.lastSeen < now - IzzyPresenceService.TTL_MS) { this.viewers.delete(key); changed = true; }
    }
    if (changed) this.refreshViewerNames();

    if (now - this.lastEchoAt > IzzyPresenceService.ECHO_TIMEOUT_MS) {
      this.scheduleResubscribe(recordId);
    }
  }

  /** Identity is not sent: the server derives who we are from the login behind the request. */
  private async announce(action: 'viewing' | 'left', recordId: string): Promise<void> {
    try {
      const provider = Metadata.Provider as GraphQLDataProvider;
      await provider.ExecuteGQL(IzzyPresenceService.PRESENCE_PUB, {
        scope: IzzyPresenceService.SCOPE,
        recordID: recordId,
        action,
      });
    } catch (e) {
      console.error('IzzyPresence: announce failed', e);
    }
  }

  private isWatching(recordId: string): boolean {
    return UUIDsEqual(this.recordId, recordId);
  }

  private closeStream(): void {
    this.stream?.unsubscribe();
    this.stream = undefined;
  }

  private refreshViewerNames(): void {
    this.viewerNames = Array.from(this.viewers.values()).map(v => v.name);
  }
}
